/* Cross-app shared location.
 *
 * The landing prayer widget (akhirahprojects.com) and this app (/prayer) run on
 * the SAME origin, so they share localStorage. The landing widget persists the
 * user's chosen location under the raw key `ap_prayer_loc_v1` with its own shape:
 *   { lat:number, lon:number, label:"City, Country", country?:string,
 *     tz?:string, school?:string, ts?:number }
 *
 * To make location carry over both ways without touching the landing repo, this
 * app reads AND writes that same key/shape. The widget remains the unchanged
 * owner of its key; we just stay shape-compatible. (The widget only requires
 * numeric `lat`/`lon` on read, and we preserve any extra fields like `school`.)
 */
import type { Location } from './types';

export const SHARED_LOC_KEY = 'ap_prayer_loc_v1';

interface WidgetLoc {
  lat: number;
  lon: number;
  label?: string;
  country?: string;
  tz?: string;
  school?: string;
  ts?: number;
  [k: string]: unknown;
}

function tzFallback(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Read the shared widget location and map it to this app's Location shape. */
export function readSharedLocation(): Location | null {
  try {
    const raw = localStorage.getItem(SHARED_LOC_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as WidgetLoc;
    if (!v || typeof v.lat !== 'number' || typeof v.lon !== 'number') return null;

    const parts = String(v.label ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const country = (typeof v.country === 'string' && v.country.trim()) || parts[parts.length - 1] || '';
    let city = parts[0] || country || 'Selected location';
    // Guard against the widget's "Lat 31.42, Lon 73.08" fallback labels.
    if (/^lat\s/i.test(city)) city = country || 'Selected location';

    const timezone = (typeof v.tz === 'string' && v.tz.trim()) || tzFallback();
    return { city, country, latitude: v.lat, longitude: v.lon, timezone };
  } catch {
    return null;
  }
}

/** Write this app's location back into the shared widget key, preserving extra widget fields. */
export function writeSharedLocation(loc: Location): void {
  try {
    let prev: WidgetLoc = {} as WidgetLoc;
    try {
      const raw = localStorage.getItem(SHARED_LOC_KEY);
      if (raw) prev = (JSON.parse(raw) as WidgetLoc) ?? ({} as WidgetLoc);
    } catch {
      /* ignore */
    }
    const label = loc.country ? `${loc.city}, ${loc.country}` : loc.city;
    const next: WidgetLoc = {
      ...prev, // keep widget-owned fields like `school`
      lat: loc.latitude,
      lon: loc.longitude,
      label,
      country: loc.country,
      tz: loc.timezone,
      ts: Date.now()
    };
    localStorage.setItem(SHARED_LOC_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}
